import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next'

// Route to readable name mapping
const routeNames = {
    'dashboard': 'Dashboard',
    'projects': 'Projects',
    'funding': 'Funding',
    'requests': 'Requests',
    'approval': 'Approval',
    'allocation': 'Allocation',
    'finance': 'Finance',
    'reports': 'Reports',
    'budget': 'Budget Tracking',
    'expenses': 'Expense Management',
    'team': 'Team',
    'management': 'User Management',
    'consultant': 'Consultant',
    'consultants': 'Consultants',
    'recommendations': 'Recommendations',
    'profile-settings': 'Profile Settings',
    'preferences': 'Preferences',
    'hfo': 'HIV Financing Observatory (HFO)'
};

// Get readable name for a path segment
function getSegmentName(segment) {
    // Check if it's a UUID or ID (numbers)
    if (/^\d+$/.test(segment) || /^[a-f0-9-]{36}$/i.test(segment)) {
        return 'Details';
    }
    return routeNames[segment] || segment.charAt(0).toUpperCase() + segment.slice(1).replace(/-/g, ' ');
}

// Generate breadcrumb items from pathname
function generateBreadcrumbs(pathname) {
    const segments = pathname.split('/').filter(Boolean);
    const breadcrumbs = [];
    let currentPath = '';

    segments.forEach((segment, index) => {
        currentPath += `/${segment}`;
        breadcrumbs.push({
            name: getSegmentName(segment),
            path: currentPath,
            isLast: index === segments.length - 1
        });
    });

    return breadcrumbs;
}

// Home Icon
function HomeIcon({ className = '' }) {
    return (
        <svg className={`w-4 h-4 ${className}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
        </svg>
    );
}

// Chevron Separator
function ChevronSeparator() {
    return (
        <svg className="w-4 h-4 text-gray-400 mx-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
    );
}

export default function Breadcrumb({ className = '' }) {
  const { t } = useTranslation('ui')
    const location = useLocation();
    const breadcrumbs = generateBreadcrumbs(location.pathname);

    // Don't show breadcrumbs on dashboard (home)
    if (location.pathname === '/dashboard' || location.pathname === '/') {
        return null;
    }

    return (
        <nav
            className={`flex items-center text-sm ${className}`}
            aria-label={t('aria_breadcrumb')}
        >
            {/* Home Link */}
            <Link
                to="/dashboard"
                className="flex items-center text-gray-500 hover:text-blue-600 transition-colors"
                aria-label={t('aria_home')}
            >
                <HomeIcon />
            </Link>

            <ChevronSeparator />

            {/* Breadcrumb items */}
            <ol className="flex items-center">
                {breadcrumbs.map((crumb, index) => (
                    <li key={crumb.path} className="flex items-center">
                        {index > 0 && <ChevronSeparator />}

                        {crumb.isLast ? (
                            <span
                                className="text-gray-900 dark:text-gray-100 font-medium"
                                aria-current="page"
                            >
                                {crumb.name}
                            </span>
                        ) : (
                            <Link
                                to={crumb.path}
                                className="text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 transition-colors"
                            >
                                {crumb.name}
                            </Link>
                        )}
                    </li>
                ))}
            </ol>
        </nav>
    );
}
